import {isPdfLink, normalizePdfUri} from "./pdf";

function escapeHtml(str) {
	return String(str)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function escapeRegExp(str) {
	return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function isContactSupportLink(url) {
	return /contact-support/i.test(String(url || ""));
}

function decodePath(url) {
	try {
		return decodeURIComponent(normalizePdfUri(url) || String(url || ""));
	} catch (e) {
		return normalizePdfUri(url) || String(url || "");
	}
}

function pathLooksLikeGuide(url) {
	const u = decodePath(url);
	return (
		/\/guide(\/|\?|$)/i.test(u) ||
		/\/juhend\//i.test(u) ||
		/руководств/i.test(u) ||
		/\/mdrik\//i.test(u) ||
		/\/מדריך\//i.test(u)
	);
}

function pathLooksLikeResources(url) {
	const u = decodePath(url);
	return (
		/\/resources(\/|\?|$)/i.test(u) ||
		/\/lisafailid\//i.test(u) ||
		/дополнительн/i.test(u) ||
		/\/download/i.test(u)
	);
}

function fileName(url) {
	const u = normalizePdfUri(url).split("?")[0];
	const parts = u.split("/");
	return parts[parts.length - 1] || "";
}

function urlsMatch(a, b) {
	const na = normalizePdfUri(a);
	const nb = normalizePdfUri(b);
	if (!na || !nb) {
		return false;
	}
	if (na === nb) {
		return true;
	}
	const fa = fileName(na);
	const fb = fileName(nb);
	return Boolean(fa) && fa === fb;
}

function findCatalogMatch(files, url, text) {
	if (!Array.isArray(files)) {
		return null;
	}
	return (
		files.find((f) => urlsMatch(f.filePath, url)) ||
		files.find((f) => text && f.displayName === text) ||
		null
	);
}

export function localizedAppPath(language, pathname) {
	const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
	if (typeof window !== "undefined") {
		const match = window.location.pathname.match(
			/^\/(en|ru|et|fi|he|iw)(?=\/|$)/
		);
		if (match) {
			return `/${match[1]}${path}`;
		}
	}
	if (language && language !== "et") {
		return `/${language}${path}`;
	}
	return path;
}

export function classifyNotificationLink(item, catalogs = {}) {
	const raw = (item && item.link) || "";
	const text = (item && item.text) || "";
	const url = normalizePdfUri(raw);
	const guides = catalogs.guides || [];
	const resources = catalogs.resources || [];

	if (isContactSupportLink(raw) || isContactSupportLink(url)) {
		return {section: "feedback"};
	}

	const matchGuide = findCatalogMatch(guides, url, text);
	if (matchGuide) {
		return {
			section: "guide",
			pdf: normalizePdfUri(matchGuide.filePath) || matchGuide.filePath,
			title: matchGuide.displayName || text,
		};
	}

	const matchRes = findCatalogMatch(resources, url, text);
	if (matchRes) {
		return {
			section: "resources",
			pdf: normalizePdfUri(matchRes.filePath) || matchRes.filePath,
			title: matchRes.displayName || text,
		};
	}

	if (pathLooksLikeResources(url) || pathLooksLikeResources(raw)) {
		return {
			section: "resources",
			pdf: isPdfLink(url) ? url : null,
			title: text,
		};
	}

	if (pathLooksLikeGuide(url) || pathLooksLikeGuide(raw) || isPdfLink(url)) {
		return {
			section: "guide",
			pdf: isPdfLink(url) ? url : null,
			title: text,
		};
	}

	return {section: "external", href: url || raw};
}

export function buildNotificationHref(classification, language) {
	if (!classification) {
		return "#";
	}
	if (classification.section === "feedback") {
		return localizedAppPath(language, "/app/feedback");
	}
	if (
		classification.section === "guide" ||
		classification.section === "resources"
	) {
		const base =
			classification.section === "guide" ? "/app/guide" : "/app/resources";
		const path = localizedAppPath(language, base);
		if (!classification.pdf) {
			return path;
		}
		const params = new URLSearchParams({
			pdf: classification.pdf,
			text: classification.title || "",
		});
		return `${path}/?${params.toString()}`;
	}
	return classification.href || "#";
}

function replaceFirst(haystack, needle, replacement) {
	const index = haystack.indexOf(needle);
	if (index === -1) {
		return haystack;
	}
	return (
		haystack.slice(0, index) + replacement + haystack.slice(index + needle.length)
	);
}

/**
 * Turn API notification text + link map into HTML anchors that open
 * Guide / Resources PDF viewers (or Personal Support).
 *
 * options.inlinePdfViewer: when true, PDF links stay on the current page
 * (data-pdf attributes) so Back can restore the diary message like the app.
 */
export function renderNotificationHtml(
	text,
	mapItems,
	catalogs,
	language,
	options = {}
) {
	if (!text) {
		return "";
	}
	let html = String(text);
	const items = Array.isArray(mapItems) ? mapItems : [];
	const inlinePdfViewer = Boolean(options.inlinePdfViewer);

	const jobs = items
		.map((item) => {
			const classified = classifyNotificationLink(item, catalogs);
			const label = item.text || classified.title || classified.href || "#";
			let anchor;

			if (
				inlinePdfViewer &&
				classified.pdf &&
				(classified.section === "guide" || classified.section === "resources")
			) {
				anchor = `<a class="text-green notification-pdf-link" href="#" data-pdf="${escapeHtml(
					classified.pdf
				)}" data-pdf-title="${escapeHtml(
					classified.title || label
				)}">${escapeHtml(label)}</a>`;
			} else {
				const href = buildNotificationHref(classified, language);
				const isInApp = classified.section !== "external";
				const target = isInApp ? "" : ' target="_blank" rel="noreferrer"';
				anchor = `<a class="text-green" href="${escapeHtml(
					href
				)}"${target}>${escapeHtml(label)}</a>`;
			}
			return {
				item,
				url: normalizePdfUri(item.link),
				label,
				anchor,
			};
		})
		.sort((a, b) => {
			const aLen = Math.max((a.url || "").length, (a.label || "").length);
			const bLen = Math.max((b.url || "").length, (b.label || "").length);
			return bLen - aLen;
		});

	for (const job of jobs) {
		let replaced = false;
		if (job.url) {
			const withCommas = new RegExp(`${escapeRegExp(job.url)},*`);
			if (withCommas.test(html)) {
				html = html.replace(withCommas, job.anchor);
				replaced = true;
			} else if (job.item.link && html.includes(job.item.link)) {
				html = replaceFirst(html, job.item.link, job.anchor);
				replaced = true;
			}
		}
		if (!replaced && job.label && html.includes(job.label)) {
			html = replaceFirst(html, job.label, job.anchor);
		}
	}

	return html;
}

export function readPdfQuery(search) {
	const params = new URLSearchParams(search || "");
	const pdf = normalizePdfUri(params.get("pdf") || "");
	const text = params.get("text") || "";
	return {pdf: pdf || null, text: text || null};
}
